package fyi.moko.hotpl.ui.account

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import fyi.moko.hotpl.ui.isIosPlatform
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fyi.moko.hotpl.domain.model.Emotion
import fyi.moko.hotpl.domain.usecase.LinkAccountUseCase
import fyi.moko.hotpl.ui.theme.dotColor
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AccountScreen(
    onBack: () -> Unit,
    viewModel: AccountViewModel = koinViewModel(),
) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    // Bound to the TextFields directly — see AccountViewModel.emotionLabelDrafts.
    val emotionLabelDrafts by viewModel.emotionLabelDrafts.collectAsStateWithLifecycle()
    val usageTrackingEnabled by viewModel.usageTrackingEnabled.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    val launcher = rememberSocialSignInLauncher()

    LaunchedEffect(Unit) {
        viewModel.uiEvents.collect { event ->
            when (event) {
                is AccountUiEvent.ShowMessage -> snackbarHostState.showSnackbar(event.message)
            }
        }
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { Text("Account") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                    }
                },
            )
        },
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .verticalScroll(rememberScrollState())
                .padding(horizontal = 24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            Spacer(Modifier.height(24.dp))

            if (state.isAnonymous) {
                Text("Guest account", style = MaterialTheme.typography.titleMedium)
                Text(
                    "Your places live on this device and sync anonymously. Link an account to " +
                        "keep them if you switch devices.",
                    style = MaterialTheme.typography.bodyMedium,
                )
            } else {
                Text("Signed in", style = MaterialTheme.typography.titleMedium)
                Text(
                    state.email ?: "Your account is linked.",
                    style = MaterialTheme.typography.bodyMedium,
                )
            }

            Spacer(Modifier.height(8.dp))

            if (state.isLinking) {
                CircularProgressIndicator()
            } else if (state.isAnonymous) {
                Button(
                    onClick = {
                        viewModel.startLinking()
                        launcher.signIn(LinkAccountUseCase.Provider.GOOGLE) { result ->
                            viewModel.completeLink(LinkAccountUseCase.Provider.GOOGLE, result)
                        }
                    },
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Text("Continue with Google")
                }

                // Apple sign-in only has a native implementation on iOS (see
                // SocialSignInLauncher.android.kt / .js.kt, which fail every provider but
                // Google); showing the button on other platforms would just be a guaranteed
                // "Only Google sign-in is available" error.
                if (isIosPlatform) {
                    OutlinedButton(
                        onClick = {
                            viewModel.startLinking()
                            launcher.signIn(LinkAccountUseCase.Provider.APPLE) { result ->
                                viewModel.completeLink(LinkAccountUseCase.Provider.APPLE, result)
                            }
                        },
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        Text("Continue with Apple")
                    }
                }
            } else {
                // Signed in (non-anonymous) — offer sign-out so a wrong account can be
                // exited without uninstalling / clearing site data (backlog 0-6).
                OutlinedButton(
                    onClick = { viewModel.signOut() },
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Text("Sign out")
                }
            }

            Spacer(Modifier.height(8.dp))

            EmotionLabelSection(
                drafts = emotionLabelDrafts,
                onChange = viewModel::onEmotionLabelChange,
                onReset = viewModel::resetEmotionLabel,
            )

            Spacer(Modifier.height(24.dp))

            UsageTrackingSection(
                enabled = usageTrackingEnabled,
                onToggle = viewModel::setUsageTrackingEnabled,
            )

            Spacer(Modifier.height(24.dp))

            // Lets you confirm which build is actually running (web deploys in
            // particular are otherwise unverifiable from the UI). Placed inline
            // rather than pinned to the bottom via a weight(1f) spacer — mobile
            // browsers' 100vh/100dvh mismatches made a bottom-pinned version of
            // this render below the visible fold on some phones.
            Text(
                "Build ${fyi.moko.hotpl.BuildInfo.GIT_SHA}",
                style = MaterialTheme.typography.labelSmall,
            )
        }
    }

    state.pendingSwitchDecision?.let { pending ->
        AlertDialog(
            onDismissRequest = { /* must choose one of the two options below */ },
            title = { Text("Existing places found") },
            text = {
                Text(
                    "You have ${pending.localPlaceCount} place(s) saved on this device. Keep them and " +
                        "add them to this account, or use this account's existing places instead?",
                )
            },
            confirmButton = {
                Button(onClick = { viewModel.resolveSwitchChoice(keepLocalData = true) }) {
                    Text("Keep my places")
                }
            },
            dismissButton = {
                OutlinedButton(onClick = { viewModel.resolveSwitchChoice(keepLocalData = false) }) {
                    Text("Use account's places")
                }
            },
        )
    }
}

/**
 * The 0-39 usage-log switch. Plain words about what goes out, because the honest version
 * ("which chips you tap, which screens you open — never your notes, photos or place names")
 * is also the reassuring one.
 */
@Composable
private fun UsageTrackingSection(enabled: Boolean, onToggle: (Boolean) -> Unit) {
    Column(
        modifier = Modifier.fillMaxWidth().testTag("usage-tracking-section"),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                "Share how you use the app",
                style = MaterialTheme.typography.titleMedium,
                modifier = Modifier.weight(1f),
            )
            Switch(
                checked = enabled,
                onCheckedChange = onToggle,
                modifier = Modifier.testTag("usage-tracking-switch"),
            )
        }
        Text(
            "Sends the developer which buttons and screens you use, so the app can be made " +
                "simpler around what you actually do. Never your notes, photos or place names. " +
                "This device only.",
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

/**
 * Rename the eight emotion slots. The color dot and the order stay — only the word changes
 * (see [fyi.moko.hotpl.domain.service.EmotionLabelPreference]). An empty field means the
 * default name, shown as the placeholder so it never disappears from view.
 */
@Composable
private fun EmotionLabelSection(
    drafts: Map<String, String>,
    onChange: (Emotion, String) -> Unit,
    onReset: (Emotion) -> Unit,
) {
    Column(
        modifier = Modifier.fillMaxWidth().testTag("emotion-label-section"),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Text("Feeling names", style = MaterialTheme.typography.titleMedium)
        Text(
            "Call each feeling whatever fits you. Colors and order stay the same; leave a field " +
                "empty to use the default. Names live on this device only.",
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Emotion.entries.forEach { emotion ->
            val draft = drafts[emotion.id] ?: ""
            OutlinedTextField(
                value = draft,
                onValueChange = { onChange(emotion, it) },
                modifier = Modifier.fillMaxWidth().testTag("emotion-label-${emotion.id}"),
                placeholder = { Text(emotion.label) },
                singleLine = true,
                leadingIcon = {
                    Box(
                        Modifier
                            .size(12.dp)
                            .clip(CircleShape)
                            .background(emotion.dotColor),
                    )
                },
                trailingIcon = if (draft.isNotEmpty()) {
                    {
                        IconButton(onClick = { onReset(emotion) }) {
                            Icon(Icons.Filled.Close, contentDescription = "Reset ${emotion.label} to default")
                        }
                    }
                } else {
                    null
                },
            )
        }
    }
}
